import { cn } from "@/lib/cn";

interface PageHeaderProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

export function PageActions({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  if (!children) return null;
  return <div className={cn("flex flex-wrap items-center justify-end gap-2", className)}>{children}</div>;
}

export function PageHeader({ title, subtitle, actions, className }: PageHeaderProps) {
  const hasLead = title != null || subtitle != null;
  return (
    <div className={cn("flex flex-wrap items-end justify-between gap-3", className)}>
      {hasLead ? (
        <div className="min-w-0">
          {title != null ? (
            <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
          ) : null}
          {subtitle ? (
            <p className={cn("text-sm text-ink-soft", title != null && "mt-0.5")}>{subtitle}</p>
          ) : null}
        </div>
      ) : null}
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

type PageBodyWidth = "wide" | "document" | "narrow";

const PAGE_BODY_WIDTH: Record<PageBodyWidth, string> = {
  /** Fill the main pane — list pages, editors, and detail screens. */
  wide: "max-w-none",
  /** Unused leftover; prefer `wide`. Kept so existing callers type-check. */
  document: "max-w-none",
  narrow: "max-w-[800px]",
};

export function PageBody({
  children,
  className,
  width = "wide",
}: {
  children: React.ReactNode;
  className?: string;
  /** `wide` fills the main pane. `narrow` is for short placeholder screens. */
  width?: PageBodyWidth;
}) {
  return (
    <div
      data-page-width={width}
      className={cn(
        "w-full px-4 py-5 sm:px-6 sm:py-6 xl:px-8 2xl:px-10",
        width === "narrow" && "mx-auto",
        PAGE_BODY_WIDTH[width],
        className,
      )}
    >
      {children}
    </div>
  );
}
